import { Box, FormControl, FormLabel, FormErrorMessage, Text, VStack, Checkbox } from "@chakra-ui/react";
import { useState, useEffect, forwardRef, useImperativeHandle } from "react";

import { isValidIBAN } from "@bcasmart/doxecure-commons/doxecureFunctions";
import { $ok } from "foundation-ts/commons";
import { Nullable } from "foundation-ts/types";

import { G1Input } from "@components/G1Input";
import { PaymentFormRef } from "./types";

interface SepaCardProps {
    companyName: string;
    labelColor: string;
    testmode?: boolean;
    onSubmit: (consumerAccount: string, consumerName: string, captchaToken?: string) => Promise<void>;
    onValidityChange: (isValid: boolean) => void;
}

export const SepaCard = forwardRef<PaymentFormRef, SepaCardProps>((props, ref) => {
    useImperativeHandle(ref, () => ({
        submit: async (captchaToken?: string) => {
            if (!validateFields()) { return; }
            const rawIBAN = consumerAccount.replace(/\s/g, "");
            await onSubmit(rawIBAN, consumerName, captchaToken);
        }
    }));

    const { companyName, labelColor, testmode, onSubmit } = props;
    const [consumerName, setConsumerName] = useState("");
    const [consumerAccount, setConsumerAccount] = useState("");
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
        consumerName: null,
        consumerAccount: null,
    });
    const [mandateAccepted, setMandateAccepted] = useState(false);

    const mandateBg = "orange.900";
    const mandateText = "orange.100";
    const mandateBorder = "orange.600";

    function validateFields(): boolean {
        const errors: FieldErrors = {
            consumerName: null,
            consumerAccount: null,
        };

        if (consumerName.trim().length === 0) {
            errors.consumerName = "Le nom du titulaire est requis.";
        }

        const rawIBAN = consumerAccount.replace(/\s/g, "");
        if (rawIBAN.length === 0) {
            errors.consumerAccount = "L'IBAN est requis.";
        } else if (!isValidIBAN(rawIBAN, testmode)) {
            errors.consumerAccount = "L'IBAN saisi n'est pas valide.";
        }

        setFieldErrors(errors);
        return !$ok(errors.consumerName) && !$ok(errors.consumerAccount);
    }

    function handleIBANChange(e: React.ChangeEvent<HTMLInputElement>): void {
        const formatted = formatIBAN(e.target.value);
        setConsumerAccount(formatted);

        const errors: FieldErrors = {
            consumerName: null,
            consumerAccount: null,
        };
        if (formatted.length === 0) {
            errors.consumerAccount = "L'IBAN est requis";
        } else if (!isValidIBAN(formatted, testmode)) {
            errors.consumerAccount = "L'IBAN saisi n'est pas valide";
        }

        if ($ok(errors.consumerAccount)) {
            setFieldErrors(errors);
        } else if (fieldErrors.consumerAccount) {
            setFieldErrors((prev) => ({ ...prev, consumerAccount: null }));
        }
    }

    useEffect(() => {
        const isValid = consumerName.trim().length > 0 &&
                        isValidIBAN(consumerAccount.replace(/\s/g, ""), testmode) &&
                        mandateAccepted;
        props.onValidityChange(isValid);
    }, [consumerName, consumerAccount, mandateAccepted]);

    return (
        <>
            <VStack spacing={4} width="100%">
                <FormControl isRequired isInvalid={$ok(fieldErrors.consumerName)}>
                    <FormLabel fontSize="sm" color={labelColor}>Titulaire du compte bancaire</FormLabel>
                    <G1Input id="consumerName" name="consumerName" component="connection.form" variant="outline" value={consumerName}
                             size="md" textTransform="uppercase" backgroundColor="transparent"
                             onChange={(e) => {
                                setConsumerName(e.target.value);
                                if (fieldErrors.consumerName) {
                                    setFieldErrors((prev) => ({ ...prev, consumerName: null }));
                                }
                            }}
                    />
                    <FormErrorMessage color="red.200">{fieldErrors.consumerName}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={$ok(fieldErrors.consumerAccount)}>
                    <FormLabel fontSize="sm" color={labelColor}>IBAN</FormLabel>
                    <G1Input id="consumerIBAN" name="consumerIBAN" component="connection.form" variant="outline" backgroundColor="transparent" value={consumerAccount} onChange={handleIBANChange}
                             placeholder="FR76 3000 6000 0112 3456 7890 189" size="md" fontFamily="mono" letterSpacing="0.05em" maxLength={33} textTransform="uppercase" />
                    <FormErrorMessage color="red.200">{fieldErrors.consumerAccount}</FormErrorMessage>
                </FormControl>
            </VStack>

            <Box mt={5} p={4} bg={mandateBg} border="1px solid" borderColor={mandateBorder} borderRadius="md">
                <Text fontSize="xs" color={mandateText} lineHeight="1.7" mb={3}>
                    En signant ce formulaire de mandat, vous autorisez{" "}
                    <Text as="span" fontWeight="600">{companyName}</Text>{" "}
                    à envoyer des instructions à votre banque pour débiter votre compte,
                    conformément aux instructions de {companyName}. Vous bénéficiez d'un
                    droit au remboursement par votre banque selon les modalités décrites
                    dans la convention que vous avez passée avec elle. Toute demande de
                    remboursement doit être présentée dans les 8 semaines suivant la date
                    de débit de votre compte.
                </Text>
                <Checkbox isChecked={mandateAccepted} onChange={(e) => setMandateAccepted(e.target.checked)} colorScheme="orange" size="sm">
                    <Text fontSize="xs" color={mandateText} fontWeight="500">J'accepte le mandat de prélèvement SEPA</Text>
                </Checkbox>
            </Box>
        </>
    );
});

interface FieldErrors {
    consumerName?: Nullable<string>;
    consumerAccount?: Nullable<string>;
}

const formatIBAN = (value: string): string => {
    return value
        .replace(/\s/g, "")
        .toUpperCase()
        .replace(/(.{4})/g, "$1 ")
        .trim();
}